import { useAuthContext } from "../components/Auth";
import type { User } from "../types/types";

/**
 * Custom hook that provides TypeScript guarantee of non-null user for private routes.
 *
 * This hook should only be used in components that are rendered within private routes
 * where the user is guaranteed to be authenticated. It provides the same TypeScript
 * safety that `providePrivatePageProps` used to provide.
 *
 * @returns User - Guaranteed non-null user object
 * @throws Error if called outside authenticated context (should never happen in private routes)
 */
export const useAuthenticatedUser = (): User => {
  const { user } = useAuthContext();

  if (!user) {
    // This should never happen in private routes due to routing protection
    // But provides runtime safety if it somehow does
    throw new Error(
      "useAuthenticatedUser: User not authenticated in private route context"
    );
  }

  return user; // TypeScript knows this is User, not User | null
};
